import type { HgSupportedLanguage } from '@smg-real-estate/domus';

import { DEFAULT_AGENCY_PAGE_LIST_SIZE } from '@/api/customerMetaApi';
import type { DirectoryBreadcrumbLink } from '@/helpers/SEO/directoryBreadcrumb';
import { generateAgencyProfilePagePath, generateAgentProfilePagePath } from '@/helpers/Url/Url';
import type { Agency } from '@/types';
import type { AgentProfile } from '@/types/agentProfile';

export type JsonLdNode = Record<string, unknown>;

export interface SrpJsonLdGraph {
  '@context': 'https://schema.org';
  '@graph': JsonLdNode[];
}

export interface SrpJsonLdPageMeta {
  canonicalUrl: string;
  pageTitle: string;
  pageDescription: string;
  breadcrumbs: DirectoryBreadcrumbLink[];
  currentPage: number;
  totalResultsCount: number;
  language: HgSupportedLanguage;
  pageSize?: number;
}

export interface ShouldEmitSrpJsonLdParams {
  isIndexable: boolean;
  locationCount: number;
  isLoading: boolean;
  hasResults: boolean;
  canonicalUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
}

export function shouldEmitSrpJsonLd({
  isIndexable,
  locationCount,
  isLoading,
  hasResults,
  canonicalUrl,
  pageTitle,
  pageDescription,
}: ShouldEmitSrpJsonLdParams): boolean {
  return (
    isIndexable &&
    locationCount === 1 &&
    !isLoading &&
    hasResults &&
    Boolean(canonicalUrl) &&
    Boolean(pageTitle) &&
    Boolean(pageDescription)
  );
}

function buildBreadcrumbListNode(
  canonicalUrl: string,
  breadcrumbs: DirectoryBreadcrumbLink[],
): JsonLdNode {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(crumb.link ? { item: crumb.link } : {}),
    })),
  };
}

function buildCollectionPageNode({
  canonicalUrl,
  pageTitle,
  pageDescription,
}: Pick<SrpJsonLdPageMeta, 'canonicalUrl' | 'pageTitle' | 'pageDescription'>): JsonLdNode {
  return {
    '@type': 'CollectionPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    description: pageDescription,
    breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
    mainEntity: { '@id': `${canonicalUrl}#itemlist` },
  };
}

function buildItemListNode({
  canonicalUrl,
  totalResultsCount,
  itemListElement,
}: {
  canonicalUrl: string;
  totalResultsCount: number;
  itemListElement: JsonLdNode[];
}): JsonLdNode {
  return {
    '@type': 'ItemList',
    '@id': `${canonicalUrl}#itemlist`,
    numberOfItems: totalResultsCount,
    itemListElement,
  };
}

function buildPagedListItems(entityIds: unknown[], startPosition: number): JsonLdNode[] {
  return entityIds.map((entityId, index) => ({
    '@type': 'ListItem',
    position: startPosition + index + 1,
    item: { '@id': entityId },
  }));
}

function assembleSrpJsonLdGraph(
  meta: SrpJsonLdPageMeta,
  itemListElement: JsonLdNode[],
  additionalNodes: JsonLdNode[],
): SrpJsonLdGraph {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildCollectionPageNode(meta),
      buildBreadcrumbListNode(meta.canonicalUrl, meta.breadcrumbs),
      buildItemListNode({
        canonicalUrl: meta.canonicalUrl,
        totalResultsCount: meta.totalResultsCount,
        itemListElement,
      }),
      ...additionalNodes,
    ],
  };
}

function buildOrganizationNode({
  url,
  name,
  image,
}: {
  url: string;
  name: string;
  image?: string | null;
}): JsonLdNode {
  const organization: JsonLdNode = {
    '@type': 'Organization',
    '@id': url,
    name,
    url,
  };

  if (image) {
    organization.image = image;
  }

  return organization;
}

function resolveParentAgencyUrl(
  agent: AgentProfile,
  language: HgSupportedLanguage,
): string | undefined {
  if (!agent.headAgencyId || !agent.profilePageUrlKeyword || !agent.agencyName) {
    return undefined;
  }

  return generateAgencyProfilePagePath(language, agent.profilePageUrlKeyword, agent.headAgencyId);
}

function ensureOrganizationNode(
  organizationById: Map<string, JsonLdNode>,
  organization: { url: string; name: string; image?: string | null },
): void {
  if (organizationById.has(organization.url)) {
    return;
  }

  organizationById.set(organization.url, buildOrganizationNode(organization));
}

export function buildAgencySrpJsonLdGraph(
  agencies: Agency[],
  meta: SrpJsonLdPageMeta,
): SrpJsonLdGraph {
  const pageSize = meta.pageSize ?? DEFAULT_AGENCY_PAGE_LIST_SIZE;
  const startPosition = (meta.currentPage - 1) * pageSize;

  const entityNodes = agencies.map((agency) => {
    const url = generateAgencyProfilePagePath(
      meta.language,
      agency.profilePageUrlKeyword,
      agency.customerId,
    );

    return buildOrganizationNode({
      url,
      name: agency.customerName,
      image: agency.logo,
    });
  });

  const itemListElement = buildPagedListItems(
    entityNodes.map((entity) => entity['@id']),
    startPosition,
  );

  return assembleSrpJsonLdGraph(meta, itemListElement, entityNodes);
}

export function buildAgentSrpJsonLdGraph(
  agents: AgentProfile[],
  meta: SrpJsonLdPageMeta,
): SrpJsonLdGraph {
  const pageSize = meta.pageSize ?? DEFAULT_AGENCY_PAGE_LIST_SIZE;
  const startPosition = (meta.currentPage - 1) * pageSize;

  const organizationById = new Map<string, JsonLdNode>();
  const entityNodes: JsonLdNode[] = [];

  for (const agent of agents) {
    const agentUrl = generateAgentProfilePagePath(meta.language, agent);
    const agentName = `${agent.firstName} ${agent.lastName}`.trim();
    const agentNode: JsonLdNode = {
      '@type': ['Person', 'RealEstateAgent'],
      '@id': agentUrl,
      name: agentName,
      url: agentUrl,
    };

    if (agent.avatarUrl) {
      agentNode.image = agent.avatarUrl;
    }

    const agencyUrl = resolveParentAgencyUrl(agent, meta.language);
    if (agencyUrl) {
      ensureOrganizationNode(organizationById, {
        url: agencyUrl,
        name: agent.agencyName,
        image: agent.agencyLogoUrl,
      });
      agentNode.worksFor = { '@id': agencyUrl };
    }

    entityNodes.push(agentNode);
  }

  const itemListElement = buildPagedListItems(
    entityNodes.map((entity) => entity['@id']),
    startPosition,
  );

  return assembleSrpJsonLdGraph(meta, itemListElement, [
    ...organizationById.values(),
    ...entityNodes,
  ]);
}

export function toJsonLdScript(graph: SrpJsonLdGraph | null): Array<{
  type: 'application/ld+json';
  textContent: string;
}> {
  if (!graph) {
    return [];
  }

  return [
    {
      type: 'application/ld+json' as const,
      textContent: JSON.stringify(graph),
    },
  ];
}
